/* eslint-disable max-lines */
// about axios
// Copyright (c) 2014-present Matt Zabriskie
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

// about whatwg-fetch
// Copyright (c) 2014-2016 GitHub, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining
// a copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to
// permit persons to whom the Software is furnished to do so, subject to
// the following conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
// LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
// OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
// WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import Error from '@/common/nodejs/errors';
const axios = require('axios').default;
const session = require('../../session');
const DISCONNECT_LIMIT = 60000;
import 'whatwg-fetch';
import logWrapper from '../../logWrapper';
import util from '../../util/utils';

const MAX_RETRY_COUNT = Number(process.env.VUE_APP_COMMON_API_RETRY_MAX_COUNT);

/**
 * トレーニングモード
 */
let IS_TEST_MODE = false;
let testModeData = null;
let requestApiType = null; // APIrequestType
/**
 * トレーニングモード時のWSP系でどのtypeの実行をしたか保持
 */
let TEST_MODE_WSP_TYPE = null;

const createErrorResponseData = (errorInfo) => {
  logWrapper.log("createErrorResponseData", errorInfo);
  // 初期値のデータを生成
  const result = { data: { code: null, error: errorInfo }, status: null };
  if (window.navigator.onLine) {
    result.data.code = Error.FRONT_SERVER_NO_RESPONCE;
  } else {
    result.data.code = Error.FRONT_DISCONNECT_NETWORK;
  }
  // HTTP Status code が取得できる場合は値を設定する
  if (errorInfo.status) {
    result.status = errorInfo.status;
  }
  return result;
};

const checkRetryExecution = (response) => {
  let result = false;
  logWrapper.log("checkRetryExecution response ", response);
  // 応答のステータスコードが 5XX 系ならリトライする
  if (response) {
    logWrapper.log("checkRetryExecution data ", response.data);
    if (500 <= response.status && response.status <= 511) {
      if (typeof response.data === 'string') {
        logWrapper.log("checkRetryExecution data is string ", response.data);
        result = true;
      } else {
        if (500 <= response.data.code && response.data.code <= 511) {
          logWrapper.log("checkRetryExecution data.code is 5xx ", response.data.code);
          result = true;
        }
      }
    }
  }
  return result;
};

const ApiService = {
  // APIの実行先
  ADMIN: 'admin',
  REPRO: 'repro',
  MASTER: 'master',
  MAINT: 'maintenance',
  GPLM: 'gplm',
  MAC: 'mac',
  IMB: 'imb',
  WSP: 'wsp',
  VCH: 'vch',
  CST: 'cst',
  CNC: 'cnc',
  DMAT: 'dmat',
  DIAG: 'dig',
  OBD: 'obd',

  init(module_name) {
    // 中国開発環境のみBaseURLを設定しない
    if (!process.env.VUE_APP_DEV_ENVIRONMENT) {
      axios.defaults.baseURL = this.createBaseURL();
    }
    axios.defaults.headers.common['x-repro-module'] = module_name;
    axios.defaults.headers.common['x-csrf-repro'] = 'SSM5';
    axios.defaults.headers.common['Pragma'] = 'no-cache';
    axios.defaults.headers.post['Content-Type'] = 'application/json';
    axios.defaults.crossDomain = true;
    //axios.defaults.withcredentials = true;

    // Add a response interceptor
    axios.interceptors.response.use(
      /* eslint-disable-next-line */
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
      },
      /* eslint-disable-next-line */
      async function (error) {
        logWrapper.log(error);
        if (!error.response) {
          return createErrorResponseData(error);
        }

        const response = error.response;

        // GET リトライ処理
        if (checkRetryExecution(response)) {
          try {
            // リトライ上限超過
            if (error.config.retryCount >= MAX_RETRY_COUNT) {
              return createErrorResponseData(response);
            }
            // リトライ上限内
            if (Number.isInteger(error.config.retryCount))
              error.config.retryCount += 1;
            else
              error.config.retryCount = 1;

            logWrapper.log("retryCount : ", error.config.retryCount);
            await util.sleep(process.env.VUE_APP_COMMON_API_RETRY_INTERVAL);
            return axios.request(error.config);
          } catch (error) {
            return createErrorResponseData(response);
          }
        }

        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        // console.log(error.response);
        if (response && response.data && typeof response.data === 'string') {
          try {
            const parseData = JSON.parse(response.data);
            return { data: parseData };
          } catch (error) {
            // JSONのparseに失敗した場合はエラーのリクエストを返却する。
            return createErrorResponseData(response);
          }
        }

        // response内にデータが存在していた場合はそのまま返す
        if (response.data) {
          return response;
        }
        else {
          return createErrorResponseData(response);
        }
      }
    );
  },

  /**
   * CCのテストモード設定
   * @param {boolean} isTestMode
   */
  setPairingAppMode(isTestMode) {
    // トレーニングモードの場合jsonfileを取得
    if (isTestMode && testModeData === null) {
      testModeData = {
        dig: require('../../../../execute/public/trainingModeData/diag.json'),
        wsp: require('../../../../execute/public/trainingModeData/wsp.json'),
        dmat: require('../../../../execute/public/trainingModeData/dmat.json'),
      };
    }
    IS_TEST_MODE = isTestMode;
  },

  /**
   * テストモード取得
   * @returns {boolean} テストモード設定
   */
  getPairingAppMode() {
    return IS_TEST_MODE;
  },

  getCall(tyep, endPoint, operationScreen = null) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('GET', endURL, null, false, operationScreen);
  },

  async getPaginateDataCall(tyep, endPoint, key, in_params = {}) {
    /* eslint-disable prefer-const */
    let result = await this.getPaginateData(key, in_params, async (params) => {
      let res = await this.getParamCall(tyep, endPoint, params);
      return res;
    });
    /* eslint-enable */
    return result;
  },

  getParamCall(tyep, endPoint, param, operationScreen = null) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('GET', endURL, param, false, operationScreen);
  },

  postCall(tyep, endPoint, param, operationScreen = null) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('POST', endURL, param, false, operationScreen);
  },

  patchCall(tyep, endPoint, param, operationScreen = null) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('PATCH', endURL, param, false, operationScreen);
  },

  deleteCall(tyep, endPoint, param = null) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('DELETE', endURL, param);
  },

  // 現状呼び出されていない
  putCall(tyep, endPoint, param) {
    /* eslint-disable-next-line */
    let endURL = this.createRequestURL(tyep, endPoint);
    return this.sendRequestToServer('PUT', endURL, param);
  },

  uploadFileToS3(endPoint, param) {
    return window.fetch(endPoint, {
      method: 'PUT',
      body: param
    });
  },

  uploadBlobToFile(endpoint, blob) {
    delete axios.defaults.headers.put['Content-Type'];
    delete axios.defaults.headers.common['Authorization'];
    return axios.put(endpoint, blob);
  },

  createRequestURL(type, url) {
    // トレーニングモードの場合のみtype保存する
    if (IS_TEST_MODE) {
      requestApiType = type;
    }
    let pathURL;
    const serviceInfo = SERVICE_MAPPING[type];

    if (process.env.VUE_APP_DEV_ENVIRONMENT && serviceInfo?.devEnv) {
      pathURL = process.env.VUE_APP_SERVER_BASE_URL.format(serviceInfo?.devEnv);
    } else {
      pathURL = serviceInfo?.dest;
    }

    // const resultUrl = pathURL + process.env.VUE_APP_SERVER_PATH + url;
    // return resultUrl;
    return pathURL ? pathURL + url : url;
  },

  async getPaginateData(types, params, callback) {

    let result = [];
    let response = {};

    // ページ分割パラメータ
    // frontend からlimitは設定しない。
    // params.limit = 1;
    params.last_evaluated_key = null;

    if (callback) {
      /* eslint-disable-next-line */
      while (true) {
        response = await callback(params);
        if (!this.isSuccessResponse(response.data)) {
          // エラーが発生した場合は途中で処理を終了する
          return response;
        }
        else {
          const data = response.data[types];
          if (data && data.length > 0) {
            result = result.concat(response.data[types]);
          }
        }

        // 次のデータが存在するかチェックする
        if (!response.data.last_evaluated_key) {
          // 次のデータが存在しないか
          break;
        }
        else {
          params.last_evaluated_key = JSON.stringify(response.data.last_evaluated_key);
        }
      }
    }

    response.data[types] = result;
    return response;
  },

  setTokenToHeader() {
    const token = session.getToken();
    if (token) {
      axios.defaults.headers.common['Authorization'] = token.id_token;
    }

    const loginToken = session.getLoginToken();
    if (loginToken) {
      axios.defaults.headers.common['x-repro-session-token'] = loginToken.session_token;
    }
  },

  /* eslint-disable-next-line */
  async sendRequestToServer(method, endPoint, param = null, tokenRetry = false, operationScreen = null) {
    // Restart idle timer.
    session.restartSessionTimer();

    let apiResponse = null;
    // 保存データ画面でなくかつテストモードで実行中の場合apiを実行せずフロントエンドで作成したresponseデータを返す
    if (IS_TEST_MODE && !this.isTestModeIgnorePath()) {
      //実行されたAPIを判断
      apiResponse = this.findMatchingData(endPoint, method, param);
      // レスポンス用のjsonが見つからない場合は、テストモードでも通常のAPIを実行する
      if (apiResponse) {
        return apiResponse;
      }
    }

    this.setTokenToHeader();
    // operationScreenが存在する場合、呼び出し元の機能が診断/データ再生どちらか判別するためヘッダーに設定
    // HACK: axios.defaults はグローバル共有のため、並行でAPIリクエスト時に screen-name が別リクエストに混入する可能性がある。
    // 影響範囲が大きいため現時点では修正せず、呼び出し側で並行実行を抑止すること。
    if (operationScreen) axios.defaults.headers.common['screen-name'] = operationScreen;

    switch (method) {
      case 'GET':
        /* eslint-disable-next-line */
        let params;
        if (param) {
          params = param;
          params.timestamp = new Date().getTime();
        }
        else {
          params = { timestamp: new Date().getTime() };
        }
        // GET メソッドはタイムアウト時間を待たずに応答が返される
        apiResponse = await axios.get(endPoint, { params, timeout: DISCONNECT_LIMIT });
        break;
      case 'POST':
        apiResponse = await axios.post(endPoint, param, { timeout: DISCONNECT_LIMIT });
        break;
      case 'DELETE':
        apiResponse = await axios.delete(endPoint, { timeout: DISCONNECT_LIMIT, data: param });
        break;
      case 'PUT':
        apiResponse = await axios.put(endPoint, param);
        break;
      case 'PATCH':
        apiResponse = await axios.patch(endPoint, param, { timeout: DISCONNECT_LIMIT });
        break;
      default: return apiResponse;
    }

    // 機能判別用のヘッダーを初期化
    if (operationScreen) delete axios.defaults.headers.common['screen-name'];

    if (tokenRetry) {
      return apiResponse;
    }

    // Check whether the token is expired or not.
    if (apiResponse && apiResponse.data && typeof apiResponse.data.auth_code !== 'undefined') {
      if (apiResponse.data.auth_code === Error.EXPIRED_TOKEN) {
        /* eslint-disable-next-line */
        let endURL = this.createRequestURL('admin', '/refreshToken');
        const refreshTokenResponse = await axios.post(endURL, { refreshToken: session.getLoginToken().refresh_token });
        /* eslint-disable-next-line */
        let tokenInfo = {};
        if (process.env.VUE_APP_BUILD_ENVIRONMENT === "cn") {
          if (!refreshTokenResponse || !refreshTokenResponse.data || refreshTokenResponse.data.code > 0) {
            return { data: { code: Error.INVALID_SESSION_TOKEN } };
          }
          tokenInfo.id_token = refreshTokenResponse.data.id_token;
          tokenInfo.access_token = refreshTokenResponse.data.access_token;
        } else {
          if (!refreshTokenResponse || !refreshTokenResponse.data || !refreshTokenResponse.data.AuthenticationResult || refreshTokenResponse.data.code > 0) {
            return { data: { code: Error.INVALID_SESSION_TOKEN } };
          }
          tokenInfo.id_token = refreshTokenResponse.data.AuthenticationResult.IdToken;
          tokenInfo.access_token = refreshTokenResponse.data.AuthenticationResult.AccessToken;
        }
        session.saveToken(tokenInfo);
        return await this.sendRequestToServer(method, endPoint, param, true);
      }
    }

    return apiResponse;
  },

  createBaseURL() {
    let url = process.env.VUE_APP_SERVER_BASE_URL;

    let enviromentName = null;
    // バックアップ種別が設定されているかチェック
    if (process.env.VUE_APP_BACKUP_TYPE) {
      enviromentName = process.env.VUE_APP_BACKUP_TYPE;
    }
    else {
      enviromentName = process.env.VUE_APP_BUILD_ENVIRONMENT;
    }

    // 環境変数、ビルドバージョンが両方設定されている場合は両方の値を設定する
    if (enviromentName && process.env.VUE_APP_BUILD_VERSION) {
      // API のフォーマット例は左記 https://api-dev1-20230101.ssm-dev.com
      url = process.env.VUE_APP_SERVER_BASE_URL.format((enviromentName), ("-" + process.env.VUE_APP_BUILD_VERSION));
    }
    // ビルドバージョンだけ設定されている場合
    else if (!enviromentName && process.env.VUE_APP_BUILD_VERSION) {
      url = process.env.VUE_APP_SERVER_BASE_URL.format("", (process.env.VUE_APP_BUILD_VERSION));
    }
    // 環境変数だけ設定されている場合
    else if (enviromentName && !process.env.VUE_APP_BUILD_VERSION) {
      url = process.env.VUE_APP_SERVER_BASE_URL.format((enviromentName), "");
    }
    else {
      url = process.env.VUE_APP_SERVER_BASE_URL.format("", "");
    }
    return url;
  },

  /**
   * テストモード: 対象テストデータの取得
   * @param {string} endPoint エンドポイント
   * @param {string} method HTTP関数
   * @param {object | null} param リクエストボディ
   */
  findMatchingData(endPoint, method, param = null) {
    const fileObj = requestApiType ? testModeData[requestApiType] : null;
    // diag,wsp,dmat以外のrequestApiTypeはundefinedになるので通常APIを実行
    if (fileObj === undefined || fileObj === null) {
      return null;
    }
    const matchedKey = Object.keys(fileObj).find(key => {
      const regexPattern = key.replace(/\*/g, '[^/]+').replace(/\?/g, '\\?');
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(endPoint);
    });

    // トレーニングモードデータが見つからなかったらlogを出力 下記logが表示されている場合はBugの可能性有り
    if (!matchedKey) {
      logWrapper.log(`Not found testMode data.: ${endPoint}[${method}]`);
      return null;
    }

    // workSupportの場合はtypeも考慮する
    if ((new RegExp("/ph2-wsp/netApps/*/systems/*/workSupports/*".replace(/\*/g, '.*'))).test(endPoint) && param && param.type) {
      TEST_MODE_WSP_TYPE = param.type;
    } else if ((new RegExp("/ph2-wsp/netApps/*/systems/*/workSupports/*/requests/*".replace(/\*/g, '.*'))).test(endPoint) && TEST_MODE_WSP_TYPE) {
      return fileObj[matchedKey][method][TEST_MODE_WSP_TYPE];
    } if ((new RegExp("/ph2-wsp/netApps/*/systems/*/workSupports/13001".replace(/\*/g, '.*'))).test(endPoint) && TEST_MODE_WSP_TYPE) {
      return fileObj[matchedKey][method][TEST_MODE_WSP_TYPE];
    }

    return fileObj[matchedKey][method];
  },

  /**
   * 実行中画面がテストモード実行の対象外か判定
   * @returns {boolean} 対象外の場合trueを返す
   */
  isTestModeIgnorePath() {
    const ignorePahts = ["diag-project-list", "diag-project-details", "data-recollection"];

    // 一つでも含むものがあるか確認する
    return ignorePahts.some((pathname) => window.location.href.includes(pathname));
  }
};

const SERVICE_MAPPING = {
  [ApiService.ADMIN]: { devEnv: 'yualp8rnnf', dest: '/ph2-adm' },
  [ApiService.REPRO]: { devEnv: 'uyuz07ht5a', dest: '/ph2-rpr' },
  [ApiService.MASTER]: { devEnv: 'b0hpm2z4qg', dest: '/ph2-mst' },
  [ApiService.MAINT]: { devEnv: 'fd9334qd6g', dest: '/ph2-mnt' },
  [ApiService.GPLM]: { devEnv: 'u12vn61i2c', dest: '/ph2-gpa' },
  [ApiService.MAC]: { dest: '/ph2-mac' },
  [ApiService.IMB]: { dest: '/ph2-imb' },
  [ApiService.WSP]: { dest: '/ph2-wsp' },
  [ApiService.VCH]: { dest: '/ph2-vch' },
  [ApiService.CST]: { dest: '/ph2-cst' },
  [ApiService.CNC]: { dest: '/ph2-cnc' },
  [ApiService.DMAT]: { dest: '/ph2-dmat' },
  [ApiService.DIAG]: { dest: '/ph2-dig' },
  [ApiService.OBD]: { dest: '/ph2-obd' },
};

export default ApiService;
